import React, { useState, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import Button from "../../components/Buttons/Button";
import Checkbox from "../../components/Buttons/Checkbox/Checkbox";
import InputField from "../../components/InputFields/InputField";
import { AutoCompleteCustom } from "../../components/Autocomplete/AutoCompleteCustom";
import CustomModal from "../../components/Modals/CustomModal";
import StatusChip from "../../components/StatusChip/StatusChip";
import TypographyInline from "../../components/Typography/TypographyInline";
import IconComponent from "../../components/Icon/IconComponent";
import CircularLoader from "../../components/Loaders/CircularLoader";
import { ICON_LABELS } from "../../utils/constants/UI/iconLabels";
import {
  BRAND,
  PRIMARY,
  PURPLE,
  SUCCESS,
  ERROR,
  HELP,
  BASE,
  BORDER,
  GREY,
  TEAL,
  WARNING,
  ACTIONS,
} from "../../utils/constants/colors";
import { fontWeight, fontSize } from "../../utils/constants/UI";
import { SCROLL_BAR } from "../../utils/constants/UI/uiConstants";
import { icpEndpoints } from "../../utils/constants/httpConstants";
import {
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
} from "../../Apis/apiWrapper";
import EventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_SUCCESS, EMITTER_ERROR } from "../../utils/constants";
import { getErrorMessage } from "../../utils/helpers";
import { formatDisplayText } from "../../utils/helpers/formatDisplayText";

/* ═══════════════════ FIELD DEFINITIONS ═══════════════════ */
const COMPANY_FIELDS = [
  {
    key: "industries",
    label: "Industries / Verticals",
    placeholder:
      "Which industries do your best customers typically come from? e.g., SaaS, Retail, FinTech",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "companySize",
    label: "Company Size",
    placeholder:
      "What company size do you usually target? e.g., SMB (1 - 50), Mid-Market (51 - 1000), Enterprise (1000+)",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "revenue",
    label: "Annual Revenue Range",
    placeholder:
      "What revenue range describes your ideal customer? e.g., $1M - $10M, $50M - $200M",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
  {
    key: "regions",
    label: "Target Regions / Geography",
    placeholder:
      "Which regions or markets are you focused on? e.g., North America, UK, APAC",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "businessModel",
    label: "Business Model",
    placeholder:
      "What type of businesses do you sell to? e.g., B2B SaaS, eCommerce, Professional Services",
    defaultWeight: "nice-to-have",
    defaultSource: "both",
  },
  {
    key: "techStack",
    label: "Tech Stack / Platforms Used",
    placeholder:
      "What tools or platforms should your ideal customer already be using? e.g., Salesforce, SAP, Shopify",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
  {
    key: "growthSignals",
    label: "Growth Signals",
    placeholder:
      "What growth signals make a company a strong fit? e.g., Recently funded, expanding to new markets, growing headcount",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
  {
    key: "painSignals",
    label: "Pain Signals You Solve",
    placeholder:
      "What challenges or pain points does your ideal customer typically face?",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
  {
    key: "hiringSignals",
    label: "Hiring Signals",
    placeholder:
      "What types of roles, when actively hired, signal a company needs your solution?",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
  {
    key: "seniority",
    label: "Seniority",
    placeholder:
      "Who do you typically sell to or need buy-in from? e.g., C-Suite, VP, Director, Manager",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "jobFunction",
    label: "Job Function",
    placeholder:
      "Which teams or functions are your primary buyers or champions? e.g., Sales, IT, Operations",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "personalTriggers",
    label: "Personal Triggers",
    placeholder:
      "What signals suggest a contact is likely ready to engage? e.g., New to role, recently promoted",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "negativeSignals",
    label: "Negative / Disqualifying Signals",
    placeholder:
      "What would immediately disqualify a company from being a good fit?",
    defaultWeight: "dealbreaker",
    defaultSource: "both",
  },
  {
    key: "customQuestion",
    label: "Custom Question",
    placeholder:
      "Add a qualifying question unique to your ICP. e.g., Do they have a dedicated procurement team?",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
];

const PERSON_FIELDS = [
  {
    key: "jobTitle",
    label: "Job Title",
    placeholder: "e.g., VP of Sales, Director of Sales, Head of Revenue",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "seniority",
    label: "Seniority",
    placeholder: "e.g., VP / Director / C-Suite",
    defaultWeight: "must-have",
    defaultSource: "both",
  },
  {
    key: "department",
    label: "Department",
    placeholder: "e.g., Sales / Revenue",
    defaultWeight: "must-have",
    defaultSource: "reachiq-data",
  },
  {
    key: "decisionAuthority",
    label: "Decision Authority",
    placeholder:
      "Does this person have budget authority or influence on purchasing decisions?",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
  {
    key: "yearsExperience",
    label: "Years Experience",
    placeholder: "Minimum years of experience in current role or industry",
    defaultWeight: "nice-to-have",
    defaultSource: "reachiq-data",
  },
  {
    key: "location",
    label: "Location",
    placeholder: "e.g., US, Canada, UK, EMEA",
    defaultWeight: "nice-to-have",
    defaultSource: "reachiq-data",
  },
  {
    key: "employmentStatus",
    label: "Employment Status",
    placeholder: "e.g., Currently employed, recently changed roles",
    defaultWeight: "nice-to-have",
    defaultSource: "reachiq-data",
  },
  {
    key: "triggerRelevance",
    label: "Trigger Relevance",
    placeholder:
      "Has this person recently been promoted, changed jobs, or posted relevant content?",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
  {
    key: "negativeSignals",
    label: "Negative Signals",
    placeholder: "e.g., Intern, contractor, or in unrelated department",
    defaultWeight: "dealbreaker",
    defaultSource: "both",
  },
  {
    key: "customQuestion",
    label: "Custom Question",
    placeholder:
      "Add your own qualifying question. e.g., Has this person spoken about outbound sales, RevOps, or pipeline goals?",
    defaultWeight: "nice-to-have",
    defaultSource: "web-search",
  },
];

const WEIGHT_OPTIONS = [
  { id: "must-have", label: "Must-have" },
  { id: "nice-to-have", label: "Nice-to-have" },
  { id: "dealbreaker", label: "Dealbreaker" },
];
const SOURCE_OPTIONS = [
  { id: "web-search", label: "AI Web Search" },
  { id: "reachiq-data", label: "ReachIQ" },
  { id: "both", label: "Both Sources" },
];

const getFieldsByType = (type) =>
  type === "person" ? PERSON_FIELDS : COMPANY_FIELDS;

/* ─── DATA NORMALIZATION ─── */
const buildDefaults = (fields) => {
  const c = {},
    w = {},
    e = {},
    s = {};
  fields.forEach((f) => {
    const isEnabledByDefault = f.key !== "customQuestion";
    c[f.key] = "";
    e[f.key] = isEnabledByDefault;
    w[f.key] = isEnabledByDefault ? f.defaultWeight || "" : "";
    s[f.key] = isEnabledByDefault ? f.defaultSource || "" : "";
  });
  return { criteria: c, weights: w, enabled: e, sources: s };
};

const normalizePersona = (p) => {
  if (!p) return null;

  const fields = getFieldsByType(p.type);
  const defaults = buildDefaults(fields);
  const criteria = { ...defaults.criteria, ...(p.criteria || {}) };
  // Use only what the API returned for enabled; missing keys default to false,
  // not to the buildDefaults true-for-all value.
  const enabled = {};
  fields.forEach((f) => {
    enabled[f.key] = p.enabled ? (p.enabled[f.key] ?? false) : false;
  });
  const weights = { ...defaults.weights, ...(p.weights || {}) };
  const sources = { ...defaults.sources, ...(p.sources || {}) };

  fields.forEach((field) => {
    if (enabled[field.key]) {
      weights[field.key] = weights[field.key] || field.defaultWeight || "";
      sources[field.key] = sources[field.key] || field.defaultSource || "";
    }
  });

  return {
    ...p,
    id: p.persona_id || p.id,
    criteria,
    weights,
    enabled,
    sources,
  };
};

/* ═══════════════════ STYLES ═══════════════════ */
const useStyles = makeStyles({
  /* ── outer shell (matches CompanySavedInfo) ── */
  container: {
    margin: "16px 0px 0px 16px",
    borderRadius: "6px",
    height: "100%",
  },
  scrollWrap: {
    background: BASE.WHITE,
    height: "100%",
    overflow: "auto",
    borderRadius: "6px",
    ...SCROLL_BAR,
  },
  btnWrap: { width: "max-content", flexShrink: 0 },

  /* ── LIST header ── */
  listHeader: {
    padding: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: `1px solid ${BORDER.DEFAULT}`,
  },

  /* ── columns ── */
  columnsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "20px",
    padding: "16px",
  },
  colHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "2px",
  },
  cardsList: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginTop: "10px",
  },

  /* ── card ── */
  card: {
    border: `1px solid ${BORDER.LIGHT}`,
    borderRadius: "12px",
    background: BASE.WHITE,
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
    transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
    "&:hover": {
      borderColor: BORDER.DEFAULT,
      boxShadow: "0 6px 18px rgba(16, 24, 40, 0.08)",
      transform: "translateY(-1px)",
    },
    "&:hover $cardActions": { opacity: 1 },
  },
  cardBody: { padding: "14px 16px 12px" },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px",
    gap: "12px",
  },
  cardNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: 1,
    overflow: "hidden",
    minWidth: 0,
  },

  /* action icons row */
  cardActions: {
    display: "flex",
    gap: "2px",
    alignItems: "center",
    marginLeft: "4px",
    flexShrink: 0,
    opacity: 0.72,
    transition: "opacity 0.15s ease",
  },
  actionIcon: {
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    transition: "background 0.15s, color 0.15s",
    "&:hover": { background: BASE.BACKGROUND },
  },

  /* tags */
  tagRow: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  tag: {
    background: "#F8FAFC",
    borderRadius: "999px",
    padding: "4px 10px",
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "210px",
    border: `1px solid ${BORDER.LIGHT}`,
  },
  moreTag: {
    padding: "4px 2px",
    display: "inline-flex",
    alignItems: "center",
  },

  /* stats */
  statsRow: {
    display: "flex",
    gap: "6px",
    alignItems: "center",
    paddingTop: "10px",
    borderTop: `1px solid ${BORDER.LIGHT}`,
    flexWrap: "wrap",
  },
  statSep: {
    width: "1px",
    height: "12px",
    background: BORDER.LIGHT,
    margin: "0 2px",
  },
  statItem: { display: "flex", gap: "3px", alignItems: "center" },
  statDot: { width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0 },

  /* empty slot */
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 20px",
    gap: "6px",
    border: `2px dashed ${BORDER.DEFAULT}`,
    borderRadius: "8px",
    marginTop: "10px",
    cursor: "pointer",
    transition: "border-color 0.2s, background 0.2s",
    "&:hover": { borderColor: BRAND.PRIMARY, background: PRIMARY.W_50 },
  },

  /* ── EDITOR header ── */
  editorHeader: {
    padding: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${BORDER.DEFAULT}`,
    position: "sticky",
    top: 0,
    background: BASE.WHITE,
    zIndex: 10,
  },
  editorBtns: { display: "flex", gap: "8px" },

  /* name + type row */
  formRow: {
    display: "flex",
    gap: "16px",
    padding: "16px",
    borderBottom: `1px solid ${BORDER.DEFAULT}`,
    alignItems: "flex-end",
  },
  formFieldName: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: "1 1 50%",
  },
  formFieldType: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: "0 0 auto",
  },
  typeToggle: {
    display: "inline-flex",
    borderRadius: "8px",
    border: `1px solid ${BORDER.DEFAULT}`,
    overflow: "hidden",
    height: "36px",
  },

  /* summary */
  summaryBar: {
    margin: "16px",
    padding: "14px 16px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    border: `1px solid ${BORDER.DEFAULT}`,
  },
  summaryLeft: { flex: 1 },
  summaryStats: { display: "flex", alignItems: "center" },
  summaryStatBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 16px",
    borderLeft: `1px solid ${BORDER.DEFAULT}`,
    minWidth: "70px",
  },

  /* criteria section */
  criteriaHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px 8px",
  },
  criteriaActionBtns: { display: "flex", gap: "6px", alignItems: "center" },

  /* table */
  tableHeader: {
    display: "grid",
    gridTemplateColumns: "40px 1fr 150px 150px",
    padding: "8px 16px",
    borderBottom: `1px solid ${BORDER.DEFAULT}`,
    borderTop: `1px solid ${BORDER.DEFAULT}`,
    background: BASE.BACKGROUND_LIGHT,
    alignItems: "center",
  },
  tableRow: {
    padding: "12px 16px",
    borderBottom: `1px solid ${BORDER.LIGHT}`,
    transition: "opacity 0.15s, background 0.15s",
    "&:hover": { background: BASE.BACKGROUND_LIGHT },
  },
  rowLabelLine: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "6px",
    paddingLeft: "40px",
  },
  rowFieldsLine: {
    display: "grid",
    gridTemplateColumns: "40px 1fr 150px 150px",
    alignItems: "center",
    gap: "0",
  },
  criteriaInputWrap: { paddingRight: "20px" },
  dropdownCell: { paddingRight: "8px" },

  /* tip bar */
  tipBar: {
    margin: "14px 16px 16px",
    padding: "10px 14px",
    background: PRIMARY.W_50,
    borderRadius: "6px",
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
    border: `1px solid ${PRIMARY.W_100}`,
  },

  /* delete confirm */
  deleteBar: {
    margin: "0 16px 16px",
    padding: "12px 16px",
    background: ERROR.W_50,
    borderRadius: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: `1px solid ${ERROR.W_100}`,
  },
  deleteBarBtns: { display: "flex", gap: "8px" },
});

/* ═══════════════════ HELPERS ═══════════════════ */
const dotColor = (w) => {
  if (w === "must-have") return SUCCESS.W_500;
  if (w === "dealbreaker") return ERROR.W_500;
  if (w === "nice-to-have") return WARNING.W_500;
  return GREY.W_200; // blank/default
};

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
const ICPSettingsScreen = () => {
  const cls = useStyles();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("list");
  const [personasList, setPersonasList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFormData, setActiveFormData] = useState(null);
  const [isNewPersona, setIsNewPersona] = useState(false);
  const [deletingPersona, setDeletingPersona] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const fetchPersonas = useCallback(async () => {
    setLoading(true);
    try {
      const bustCache = `?_t=${Date.now()}`;
      const resp = await getRequest(
        navigate,
        icpEndpoints.getAllPersonas + bustCache,
      );
      if (resp?.data?.status !== false) {
        const data = (resp?.data?.data || []).map(normalizePersona);
        setPersonasList(data);
      }
    } catch (err) {
      console.error("fetchPersonas error", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  /* ─── handlers ─── */
  const handleCreateNew = useCallback(() => {
    const d = buildDefaults(COMPANY_FIELDS);
    setActiveFormData({
      id: null,
      name: "",
      type: "company",
      ...d,
    });
    setIsNewPersona(true);
    setDeletingPersona(null);
    setValidationErrors({});
    setViewMode("editor");
  }, []);

  const handleEdit = useCallback((p) => {
    setActiveFormData(JSON.parse(JSON.stringify(p)));
    setIsNewPersona(false);
    setDeletingPersona(null);
    setValidationErrors({});
    setViewMode("editor");
  }, []);

  const handleDuplicate = useCallback(
    async (p) => {
      try {
        const clone = JSON.parse(JSON.stringify(p));
        delete clone.id;
        delete clone.persona_id;
        clone.name = `${clone.name} (Copy)`;

        const resp = await postRequest(
          navigate,
          icpEndpoints.createPersona,
          clone,
        );
        if (resp?.data?.status === false) {
          throw resp;
        }
        EventEmitter.emit(EMITTER_SUCCESS, "Persona duplicated successfully");
        await fetchPersonas();
      } catch (err) {
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(err?.data?.data?.errors || {}, "Failed to duplicate"),
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchPersonas],
  );

  const openDeleteModal = useCallback((persona) => {
    setDeletingPersona(persona);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (!loading) {
      setDeletingPersona(null);
    }
  }, [loading]);

  const handleCancel = () => {
    setActiveFormData(null);
    setDeletingPersona(null);
    setViewMode("list");
  };

  const handleSave = async () => {
    const errors = {};
    if (!activeFormData.name.trim()) {
      errors.name = true;
    }

    const fields = getFieldsByType(activeFormData.type);
    const fieldMap = fields.reduce((acc, field) => {
      acc[field.key] = field;
      return acc;
    }, {});
    const enabledKeys = fields
      .filter((field) => activeFormData.enabled[field.key])
      .map((field) => field.key);
    const missingCriteria = enabledKeys.filter(
      (key) => !activeFormData.criteria[key]?.trim(),
    );

    missingCriteria.forEach((key) => {
      errors[key] = true;
    });

    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      if (errors.name) {
        EventEmitter.emit(EMITTER_ERROR, "Persona name is required");
      } else {
        EventEmitter.emit(
          EMITTER_ERROR,
          `Add criteria text for: ${missingCriteria
            .map((key) => fieldMap[key]?.label || key)
            .join(", ")}`,
        );
      }
      return;
    }

    setValidationErrors({});
    setLoading(true);

    try {
      const isEdit = activeFormData.id && !isNewPersona;
      const personaId = activeFormData.id;

      const payload = {
        name: activeFormData.name.trim(),
        type: activeFormData.type,
        criteria: {},
        weights: {},
        sources: {},
        enabled: {},
      };

      enabledKeys.forEach((key) => {
        const field = fieldMap[key];
        const criteriaValue = activeFormData.criteria[key]?.trim();
        const weightValue =
          activeFormData.weights[key] || field?.defaultWeight || "";
        const sourceValue =
          activeFormData.sources[key] || field?.defaultSource || "";

        if (criteriaValue) {
          payload.criteria[key] = criteriaValue;
          payload.enabled[key] = true;
        }
        if (criteriaValue && weightValue) {
          payload.weights[key] = weightValue;
        }
        if (criteriaValue && sourceValue) {
          payload.sources[key] = sourceValue;
        }
      });

      console.log("ICP API Payload:", JSON.stringify(payload, null, 2));

      const resp = isEdit
        ? await putRequest(
            navigate,
            icpEndpoints.updatePersona(personaId),
            payload,
          )
        : await postRequest(navigate, icpEndpoints.createPersona, payload);

      if (resp?.data?.status === false) {
        throw resp;
      }

      EventEmitter.emit(
        EMITTER_SUCCESS,
        `Persona ${isEdit ? "updated" : "created"} successfully`,
      );
      if (!isEdit) {
        window.dispatchEvent(new CustomEvent("setup:icp-auto-advance"));
      }
      setActiveFormData(null);
      setDeletingPersona(null);
      setViewMode("list");
      await fetchPersonas();
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, "Save failed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPersona?.id) return;
    setLoading(true);
    try {
      await deleteRequest(icpEndpoints.deletePersona(deletingPersona.id));
      EventEmitter.emit(EMITTER_SUCCESS, "Persona deleted successfully");
      if (activeFormData?.id === deletingPersona.id) {
        setActiveFormData(null);
      }
      setDeletingPersona(null);
      setViewMode("list");
      await fetchPersonas();
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, "Failed to delete persona");
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (key) =>
    setActiveFormData((p) => ({
      ...p,
      enabled: { ...p.enabled, [key]: !p.enabled[key] },
      weights: {
        ...p.weights,
        [key]:
          !p.enabled[key] && !p.weights[key]
            ? getFieldsByType(p.type).find((field) => field.key === key)
                ?.defaultWeight || ""
            : p.weights[key],
      },
      sources: {
        ...p.sources,
        [key]:
          !p.enabled[key] && !p.sources[key]
            ? getFieldsByType(p.type).find((field) => field.key === key)
                ?.defaultSource || ""
            : p.sources[key],
      },
    }));
  const setCriteria = (key, val) =>
    setActiveFormData((p) => ({
      ...p,
      criteria: { ...p.criteria, [key]: val },
    }));
  const setWeight = (key, val) => {
    const rawId = Array.isArray(val?.id) ? val.id[0] : val?.id;
    setActiveFormData((p) => ({
      ...p,
      weights: { ...p.weights, [key]: rawId || "" },
    }));
  };
  const setSource = (key, val) => {
    const rawId = Array.isArray(val?.id) ? val.id[0] : val?.id;
    setActiveFormData((p) => ({
      ...p,
      sources: { ...p.sources, [key]: rawId || "" },
    }));
  };

  const enableAll = () => {
    const fields = getFieldsByType(activeFormData.type);
    const ne = {};
    const nw = { ...activeFormData.weights };
    const ns = { ...activeFormData.sources };
    fields.forEach((f) => {
      ne[f.key] = true;
      nw[f.key] = nw[f.key] || f.defaultWeight || "";
      ns[f.key] = ns[f.key] || f.defaultSource || "";
    });
    setActiveFormData((p) => ({
      ...p,
      enabled: ne,
      weights: nw,
      sources: ns,
    }));
  };

  const resetDefaults = () => {
    const fields = getFieldsByType(activeFormData.type);
    const d = buildDefaults(fields);
    setActiveFormData((p) => ({
      ...p,
      weights: d.weights,
      sources: d.sources,
      enabled: d.enabled,
    }));
  };

  const switchType = (type) => {
    const fields = getFieldsByType(type);
    const d = buildDefaults(fields);
    setActiveFormData((p) => ({ ...p, type, ...d }));
  };

  /* ════════════════ LIST VIEW ════════════════ */
  const renderListView = () => {
    const company = personasList.filter((p) => p.type === "company");
    const person = personasList.filter((p) => p.type === "person");

    return (
      <Box className={cls.container}>
        <Box className={cls.scrollWrap}>
          <div className={cls.listHeader}>
            <div>
              <TypographyInline
                label="ICP & Persona Settings"
                size="md"
                fontWeight={fontWeight.MEDIUM}
                color={GREY.PRIMARY}
              />
              <div style={{ marginTop: "2px" }}>
                <TypographyInline
                  label="Define ideal customer profiles to match prospects before campaigns"
                  size="sm"
                  color={GREY.SECONDARY}
                />
              </div>
            </div>
            <div className={cls.btnWrap}>
              <Button
                size="sm36"
                variant="primary"
                label="+ New Persona"
                onClick={handleCreateNew}
              />
            </div>
          </div>

          <div className={cls.columnsGrid}>
            {/* COMPANY */}
            <div>
              <div className={cls.colHeader}>
                <IconComponent
                  iconLabel={ICON_LABELS.BUSINESS}
                  color={GREY.SECONDARY}
                  fontSize={fontSize.MD}
                />
                <TypographyInline
                  label="Company Personas"
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                />
              </div>
              <TypographyInline
                label={`${company.length} persona${company.length !== 1 ? "s" : ""}`}
                size="xs"
                color={GREY.TERTIARY}
              />
              <div className={cls.cardsList}>
                {company.map((p) => (
                  <PersonaCard
                    key={p.id}
                    cls={cls}
                    persona={p}
                    pType="company"
                    disabled={loading}
                    onEdit={() => handleEdit(p)}
                    onDuplicate={() => handleDuplicate(p)}
                    onDelete={() => openDeleteModal(p)}
                  />
                ))}
                {loading && company.length === 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "60px 20px",
                      gridColumn: "1 / -1",
                      gap: "12px",
                    }}
                  >
                    <CircularLoader size={36} loadercolor={BRAND.PRIMARY} />
                    <TypographyInline
                      label="Loading personas..."
                      size="sm"
                      color={GREY.TERTIARY}
                    />
                  </div>
                )}
                {!loading && company.length === 0 && (
                  <div
                    className={cls.emptyState}
                    onClick={handleCreateNew}
                    style={{ gridColumn: "1 / -1", padding: "48px 20px", gap: "12px" }}
                  >
                    <IconComponent
                      iconLabel={ICON_LABELS.BUSINESS}
                      color={GREY.TERTIARY}
                      fontSize="36px"
                    />
                    <TypographyInline
                      label="No company personas yet"
                      size="sm"
                      fontWeight={fontWeight.SEMIBOLD}
                      color={GREY.SECONDARY}
                    />
                    <TypographyInline
                      label="Create your first persona to define ideal customer profiles for campaign matching."
                      size="xs"
                      color={GREY.TERTIARY}
                    />
                    <div style={{ marginTop: "8px" }}>
                      <Button
                        size="sm36"
                        variant="primary"
                        label="+ Create Persona"
                        onClick={handleCreateNew}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* PERSON - hidden for now */}
            {/* <div>
              <div className={cls.colHeader}>
                <IconComponent
                  iconLabel={ICON_LABELS.PERSON}
                  color={GREY.SECONDARY}
                  fontSize={fontSize.MD}
                />
                <TypographyInline
                  label="Person Personas"
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                />
              </div>
              <TypographyInline
                label={
                  loading
                    ? "Updating..."
                    : `${person.length} persona${person.length !== 1 ? "s" : ""}`
                }
                size="xs"
                color={GREY.TERTIARY}
              />
              <div className={cls.cardsList}>
                {person.map((p) => (
                  <PersonaCard
                    key={p.id}
                    cls={cls}
                    persona={p}
                    pType="person"
                    disabled={loading}
                    onEdit={() => handleEdit(p)}
                    onDuplicate={() => handleDuplicate(p)}
                    onDelete={() => openDeleteModal(p)}
                  />
                ))}
                {loading && person.length === 0 && (
                  <div className={cls.emptyState} style={{ opacity: 0.7 }}>
                    <TypographyInline
                      label="Loading personas..."
                      size="sm"
                      color={GREY.TERTIARY}
                    />
                  </div>
                )}
                {!loading && person.length === 0 && (
                  <div className={cls.emptyState}>
                    <TypographyInline
                      label="No person personas found"
                      size="sm"
                      color={GREY.TERTIARY}
                    />
                  </div>
                )}
              </div>
            </div> */}
          </div>
        </Box>
      </Box>
    );
  };

  /* ════════════════ EDITOR VIEW ════════════════ */
  const renderEditorView = () => {
    const fields =
      activeFormData.type === "company" ? COMPANY_FIELDS : PERSON_FIELDS;
    const accent =
      activeFormData.type === "company" ? PRIMARY.W_500 : PURPLE.W_500;
    const accentLight =
      activeFormData.type === "company" ? PRIMARY.W_50 : PURPLE.W_50;
    const activeCount = Object.values(activeFormData.enabled).filter(
      Boolean,
    ).length;
    const webCount = Object.values(activeFormData.sources).filter(
      (s) => s === "web-search" || s === "both",
    ).length;
    const dataCount = Object.values(activeFormData.sources).filter(
      (s) => s === "reachiq-data" || s === "both",
    ).length;
    const wLabel = (key) => {
      const val = activeFormData.weights[key];
      if (!val) return null;
      return (WEIGHT_OPTIONS.find((o) => o.id === val) || {}).label;
    };
    const sLabel = (key) => {
      const val = activeFormData.sources[key];
      if (!val) return null;
      return (SOURCE_OPTIONS.find((o) => o.id === val) || {}).label;
    };

    return (
      <Box className={cls.container}>
        <Box className={cls.scrollWrap}>
          {/* ── header ── */}
          <div className={cls.editorHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={handleCancel}
              >
                <IconComponent
                  iconLabel={ICON_LABELS.ARROW_BACK}
                  color={GREY.SECONDARY}
                  fontSize={fontSize.MD}
                />
              </div>
              <TypographyInline
                label={isNewPersona ? "Create New Persona" : "Edit Persona"}
                size="md"
                fontWeight={fontWeight.MEDIUM}
                color={GREY.PRIMARY}
              />
            </div>
            <div className={cls.editorBtns}>
              {!isNewPersona && (
                <div className={cls.btnWrap}>
                  <Button
                    size="sm36"
                    variant="ghost"
                    label="Delete"
                    disabled={loading}
                    LeftIcon={ICON_LABELS.DELETE_OUTLINE}
                    onClick={() => openDeleteModal(activeFormData)}
                  />
                </div>
              )}
              <div className={cls.btnWrap}>
                <Button
                  size="sm36"
                  variant="ghost"
                  label="Cancel"
                  disabled={loading}
                  onClick={handleCancel}
                />
              </div>
              <div className={cls.btnWrap}>
                <Button
                  size="sm36"
                  variant="primary"
                  label={loading ? "Saving..." : "Save Persona"}
                  disabled={loading}
                  onClick={handleSave}
                />
              </div>
            </div>
          </div>

          {/* ── name + type ── */}
          <div className={cls.formRow}>
            <div className={cls.formFieldName}>
              <TypographyInline
                label="Persona Name *"
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={validationErrors.name ? ERROR.W_500 : GREY.PRIMARY}
              />
              <div
                style={
                  validationErrors.name
                    ? {
                        border: `1px solid ${ERROR.W_500}`,
                        borderRadius: "6px",
                      }
                    : {}
                }
              >
                <InputField
                  size="sm36"
                  placeholder="e.g., Mid-Market SaaS Companies"
                  value={activeFormData.name}
                  onChange={(e) => {
                    setActiveFormData({
                      ...activeFormData,
                      name: e.target.value,
                    });
                    if (validationErrors.name && e.target.value.trim()) {
                      setValidationErrors((prev) => {
                        const n = { ...prev };
                        delete n.name;
                        return n;
                      });
                    }
                  }}
                />
              </div>
              {validationErrors.name && (
                <TypographyInline
                  label="Persona name is required"
                  size="xs"
                  color={ERROR.W_500}
                />
              )}
            </div>
            <div className={cls.formFieldType}>
              <TypographyInline
                label="Persona Type *"
                size="sm"
                fontWeight={fontWeight.SEMIBOLD}
                color={GREY.PRIMARY}
              />
              <div className={cls.typeToggle}>
                <TypeBtn
                  active={activeFormData.type === "company"}
                  color={PRIMARY.W_500}
                  bgColor={PRIMARY.W_50}
                  icon={ICON_LABELS.BUSINESS}
                  label="Company"
                  disabled
                />
                {/* Person type hidden */}
                {/* <TypeBtn
                  active={activeFormData.type === "person"}
                  color={PURPLE.W_500}
                  bgColor={PURPLE.W_50}
                  icon={ICON_LABELS.PERSON}
                  label="Person"
                  onClick={() => switchType("person")}
                /> */}
              </div>
            </div>
          </div>

          {/* ── summary ── */}
          <div
            className={cls.summaryBar}
            style={{ background: accentLight, borderColor: accent + "33" }}
          >
            <div className={cls.summaryLeft}>
              <TypographyInline
                label="Matching Configuration Summary"
                size="sm"
                fontWeight={fontWeight.BOLD}
                color={accent}
              />
              <div style={{ marginTop: "2px" }}>
                <TypographyInline
                  label="Only enabled fields are included in the matching algorithm. Choose data source per field to optimize token usage."
                  size="xs"
                  color={GREY.SECONDARY}
                />
              </div>
            </div>
            <div className={cls.summaryStats}>
              <SummaryNum
                value={activeCount}
                label="Active Fields"
                color={GREY.PRIMARY}
                cls={cls}
              />
              <SummaryNum
                value={webCount}
                label="Web Search"
                color={HELP.W_500}
                cls={cls}
              />
              <SummaryNum
                value={dataCount}
                label="ReachIQ"
                color={SUCCESS.W_500}
                cls={cls}
              />
            </div>
          </div>

          {/* ── criteria header ── */}
          <div className={cls.criteriaHeader}>
            <TypographyInline
              label="Criteria & Weights"
              size="sm"
              fontWeight={fontWeight.BOLD}
              color={GREY.PRIMARY}
            />
            <div className={cls.criteriaActionBtns}>
              <div className={cls.btnWrap}>
                <Button
                  size="xs28"
                  variant="primary"
                  label="Enable All"
                  LeftIcon={ICON_LABELS.CHECK_CIRCLE}
                  onClick={enableAll}
                />
              </div>
              <div className={cls.btnWrap}>
                <Button
                  size="xs28"
                  variant="ghost"
                  label="Reset Defaults"
                  LeftIcon={ICON_LABELS.REFRESH}
                  onClick={resetDefaults}
                />
              </div>
            </div>
          </div>

          {/* ── table header ── */}
          <div className={cls.tableHeader}>
            <TypographyInline
              label="ON"
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
            />
            <TypographyInline
              label="CRITERIA"
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
            />
            <TypographyInline
              label="WEIGHT"
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
            />
            <TypographyInline
              label="DATA SOURCE"
              size="xs"
              fontWeight={fontWeight.BOLD}
              color={GREY.TERTIARY}
            />
          </div>

          {/* ── table rows ── */}
          {fields.map((field) => {
            const on = !!activeFormData.enabled[field.key];
            const rawSrc = activeFormData.sources[field.key] || "";
            let srcBorder = BORDER.DEFAULT,
              srcBg = "transparent";
            if (rawSrc === "web-search") {
              srcBorder = HELP.W_400;
              srcBg = HELP.W_50;
            } else if (rawSrc === "reachiq-data") {
              srcBorder = SUCCESS.W_400;
              srcBg = SUCCESS.W_50;
            } else if (rawSrc === "both") {
              srcBorder = PURPLE.W_400;
              srcBg = PURPLE.W_50;
            }

            return (
              <div
                key={field.key}
                className={cls.tableRow}
                style={{ opacity: on ? 1 : 0.4 }}
              >
                {/* line 1: label */}
                <div className={cls.rowLabelLine}>
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: dotColor(activeFormData.weights[field.key]),
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  <TypographyInline
                    label={field.label}
                    size="sm"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={GREY.PRIMARY}
                  />
                </div>
                {/* line 2: checkbox + input + weight + source */}
                <div className={cls.rowFieldsLine}>
                  <div>
                    <Checkbox
                      checked={on}
                      onChange={() => toggleField(field.key)}
                    />
                  </div>
                  <div
                    className={cls.criteriaInputWrap}
                    style={
                      validationErrors[field.key] && on
                        ? {
                            border: `1px solid ${ERROR.W_500}`,
                            borderRadius: "6px",
                          }
                        : {}
                    }
                  >
                    <InputField
                      size="sm36"
                      placeholder={field.placeholder}
                      value={activeFormData.criteria[field.key] || ""}
                      onChange={(e) => {
                        setCriteria(field.key, e.target.value);
                        if (
                          validationErrors[field.key] &&
                          e.target.value.trim()
                        ) {
                          setValidationErrors((prev) => {
                            const n = { ...prev };
                            delete n[field.key];
                            return n;
                          });
                        }
                      }}
                      variant={!on ? "disabled" : undefined}
                    />
                  </div>
                  <div className={cls.dropdownCell}>
                    <AutoCompleteCustom
                      items={WEIGHT_OPTIONS}
                      valueState={wLabel(field.key) ? [wLabel(field.key)] : []}
                      idState={
                        activeFormData.weights[field.key]
                          ? [activeFormData.weights[field.key]]
                          : []
                      }
                      onChange={(val) => setWeight(field.key, val)}
                      variant={!on ? "disabled" : undefined}
                      placeholder="Select Weight"
                      size="sm36"
                    />
                  </div>
                  <div
                    style={{
                      borderRadius: "6px",
                      border: `1px solid ${srcBorder}`,
                      background: srcBg,
                    }}
                  >
                    <AutoCompleteCustom
                      items={SOURCE_OPTIONS}
                      valueState={sLabel(field.key) ? [sLabel(field.key)] : []}
                      idState={
                        activeFormData.sources[field.key]
                          ? [activeFormData.sources[field.key]]
                          : []
                      }
                      onChange={(val) => setSource(field.key, val)}
                      variant={!on ? "disabled" : undefined}
                      placeholder="Select Source"
                      size="sm36"
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* tip removed */}

          {/* Bottom save bar */}
          <div
            style={{
              padding: "16px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              borderTop: `1px solid ${BORDER.DEFAULT}`,
              position: "sticky",
              bottom: 0,
              background: BASE.WHITE,
              zIndex: 10,
            }}
          >
            <div className={cls.btnWrap}>
              <Button
                size="sm36"
                variant="ghost"
                label="Cancel"
                disabled={loading}
                onClick={handleCancel}
              />
            </div>
            <div className={cls.btnWrap}>
              <Button
                size="sm36"
                variant="primary"
                label={loading ? "Saving..." : "Save Persona"}
                disabled={loading || !activeFormData.name.trim()}
                onClick={handleSave}
              />
            </div>
          </div>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {viewMode === "editor" && activeFormData
        ? renderEditorView()
        : renderListView()}

      <CustomModal
        open={!!deletingPersona}
        onClose={closeDeleteModal}
        close={closeDeleteModal}
        width="28vw"
        variant="error"
        header={{
          heading: { id: "delete_persona", label: "Delete Persona" },
        }}
        footer={{
          right: (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button
                size="sm36"
                variant="ghost"
                label="Cancel"
                disabled={loading}
                onClick={closeDeleteModal}
              />
              <Button
                size="sm36"
                variant="primary"
                label="Delete"
                isLoading={loading}
                onClick={handleDeleteConfirm}
              />
            </div>
          ),
        }}
      >
        <div style={{ padding: "8px 0" }}>
          <TypographyInline
            label={`Are you sure you want to delete "${deletingPersona?.name || "this persona"}"? This action cannot be undone.`}
            size="sm"
            color={GREY.SECONDARY}
          />
        </div>
      </CustomModal>
    </>
  );
};

/* ═══════════════════ SUB-COMPONENTS ═══════════════════ */

const TypeBtn = ({ active, color, bgColor, icon, label, onClick, disabled }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      padding: "0 20px",
      height: "100%",
      cursor: disabled ? "default" : "pointer",
      pointerEvents: disabled ? "none" : "auto",
      background: active ? bgColor : BASE.WHITE,
      borderBottom: active ? `2px solid ${color}` : "2px solid transparent",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
    }}
  >
    <IconComponent
      iconLabel={icon}
      color={active ? color : GREY.TERTIARY}
      fontSize="16px"
    />
    <TypographyInline
      label={label}
      size="sm"
      fontWeight={active ? fontWeight.SEMIBOLD : fontWeight.REGULAR}
      color={active ? color : GREY.SECONDARY}
    />
  </div>
);

const SummaryNum = ({ value, label, color, cls }) => (
  <div className={cls.summaryStatBox}>
    <TypographyInline
      label={String(value)}
      size="lg"
      fontWeight={fontWeight.BOLD}
      color={color}
    />
    <TypographyInline label={label} size="xs" color={GREY.SECONDARY} isNoWrap />
  </div>
);

const PersonaCard = ({
  cls,
  persona,
  pType,
  disabled,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const filled = Object.keys(persona.criteria).filter((k) =>
    persona.criteria[k]?.trim(),
  );
  const visible = filled.slice(0, 3);
  const more = filled.length - visible.length;

  const s = {
    mh: Object.keys(persona.weights).filter(
      (k) => persona.enabled[k] && persona.weights[k] === "must-have",
    ).length,
    nth: Object.keys(persona.weights).filter(
      (k) => persona.enabled[k] && persona.weights[k] === "nice-to-have",
    ).length,
    db: Object.keys(persona.weights).filter(
      (k) => persona.enabled[k] && persona.weights[k] === "dealbreaker",
    ).length,
    active: Object.values(persona.enabled).filter(Boolean).length,
    web: Object.keys(persona.sources).filter(
      (k) => persona.enabled[k] && persona.sources[k] === "web-search",
    ).length,
    data: Object.keys(persona.sources).filter(
      (k) => persona.enabled[k] && persona.sources[k] === "reachiq-data",
    ).length,
  };

  const stop = (e, fn) => {
    e.stopPropagation();
    if (!disabled) fn();
  };

  return (
    <div
      className={cls.card}
      onClick={!disabled ? onEdit : undefined}
      style={{
        opacity: disabled ? 0.7 : 1,
        pointerEvents: disabled ? "none" : "auto",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <div className={cls.cardBody}>
        {/* top row */}
        <div className={cls.cardTop}>
          <div className={cls.cardNameRow}>
            <TypographyInline
              label={persona.name || "Untitled"}
              size="sm"
              fontWeight={fontWeight.BOLD}
              color={GREY.PRIMARY}
              isEllipses
            />
          </div>
          {/* action icons */}
          <div className={cls.cardActions}>
            <button
              className={cls.actionIcon}
              title="Edit"
              disabled={disabled}
              onClick={(e) => stop(e, onEdit)}
            >
              <IconComponent
                iconLabel={ICON_LABELS.EDIT}
                color={GREY.TERTIARY}
                fontSize="16px"
              />
            </button>
            {/* <button
              className={cls.actionIcon}
              title="Duplicate"
              disabled={disabled}
              onClick={(e) => stop(e, onDuplicate)}
            >
              <IconComponent
                iconLabel={ICON_LABELS.CONTENT_COPY}
                color={GREY.TERTIARY}
                fontSize="14px"
              />
            </button> */}
            <button
              className={cls.actionIcon}
              title="Delete"
              disabled={disabled}
              onClick={(e) => stop(e, onDelete)}
            >
              <IconComponent
                iconLabel={ICON_LABELS.DELETE_OUTLINE}
                color={GREY.TERTIARY}
                fontSize="16px"
              />
            </button>
          </div>
          <StatusChip
            label={pType === "company" ? "Company" : "Person"}
            variant={pType === "company" ? "red" : "purple"}
          />
        </div>

        {/* tags */}
        <div className={cls.tagRow}>
          {visible.map((k) => (
            <div key={k} className={cls.tag}>
              <TypographyInline
                label={`${formatDisplayText(k)}: ${persona.criteria[k]}`}
                size="xs"
                color={GREY.SECONDARY}
                isEllipses
              />
            </div>
          ))}
          {more > 0 && (
            <div className={cls.moreTag}>
              <TypographyInline
                label={`+${more} more`}
                size="xs"
                fontWeight={fontWeight.MEDIUM}
                color={HELP.W_500}
              />
            </div>
          )}
        </div>

        {/* stats */}
        <div className={cls.statsRow}>
          <StatBadge
            cls={cls}
            count={s.mh}
            label="Must Haves"
            dotColor={SUCCESS.W_500}
          />
          <StatBadge
            cls={cls}
            count={s.nth}
            label="Nice To Haves"
            dotColor={WARNING.W_500}
          />
          <StatBadge
            cls={cls}
            count={s.db}
            label="Dealbreakers"
            dotColor={ERROR.W_500}
          />
          <div className={cls.statSep} />
          <StatBadge
            cls={cls}
            count={s.active}
            label="Active"
            dotColor={SUCCESS.W_500}
          />
          <StatBadge
            cls={cls}
            count={s.web}
            label="Web"
            dotColor={HELP.W_500}
            icon={ICON_LABELS.LANGUAGE}
          />
          <StatBadge
            cls={cls}
            count={s.data}
            label="Data"
            dotColor={TEAL.W_500}
            icon={ICON_LABELS.STORAGE}
          />
        </div>
      </div>
    </div>
  );
};

const StatBadge = ({ cls, count, label, dotColor: dc, icon }) => (
  <div className={cls.statItem}>
    <span className={cls.statDot} style={{ background: dc }} />
    <TypographyInline
      label={String(count)}
      size="xs"
      fontWeight={fontWeight.BOLD}
      color={dc}
    />
    {icon && <IconComponent iconLabel={icon} color={dc} fontSize="12px" />}
    <TypographyInline label={label} size="xs" color={GREY.SECONDARY} />
  </div>
);

export default ICPSettingsScreen;
